/** @jsxImportSource @emotion/react */
import {
  EuiButton,
  EuiFlexGroup,
  EuiForm,
  EuiSpacer,
  EuiStepsHorizontal,
  EuiStepsHorizontalProps,
} from "@inscopix/ideas-eui";
import { useState } from "react";
import { useFormikContext } from "formik";

import { FormCreatePersonalSpaceFields } from "./FormCreatePersonalSpaceFields";
import { PersonalSpaceFormFields } from "./FormCreatePersonalSpace";
import { css } from "@emotion/react";
import { FieldsByPage, PageNumbers } from "./FormCreatePersonalSpace.helpers";

export interface FormCreatePersonalSpaceBodyProps {
  fieldsByPage: FieldsByPage;
}

export const FormCreatePersonalSpaceBody = ({
  fieldsByPage,
}: FormCreatePersonalSpaceBodyProps) => {
  const [pagesVisited, setPagesVisited] = useState(
    new Set<PageNumbers[number]>([1]),
  );
  const { getFieldMeta, isValid, handleSubmit, isSubmitting } =
    useFormikContext<PersonalSpaceFormFields>();

  const [page, setPage] = useState<PageNumbers[number]>(1);

  const isPageComplete = (page: PageNumbers[number]) => {
    return (fieldsByPage.get(page)?.fields ?? ([] as string[])).reduce(
      (isValid, fieldKey) => {
        const meta = getFieldMeta(fieldKey);
        if (meta.error !== undefined) {
          return false;
        }
        return isValid;
      },
      true,
    );
  };

  const getTimelineStatus = (
    timelinePage: PageNumbers[number],
  ): EuiStepsHorizontalProps["steps"][number]["status"] => {
    const isComplete = isPageComplete(timelinePage);
    if (page === timelinePage) {
      return "current";
    }

    if (pagesVisited.has(timelinePage) && isComplete) {
      return "complete";
    }

    if (!isComplete || (isComplete && !pagesVisited.has(timelinePage))) {
      return "incomplete";
    }

    return "disabled";
  };

  const handleSetPage = (targetPage?: PageNumbers[number]) => {
    if (page === targetPage) {
      return;
    }

    if (targetPage !== undefined) {
      setPage(targetPage);
      setPagesVisited((prev) => new Set(prev).add(targetPage));
    } else {
      setPage((prev) => {
        const nextPage =
          prev === fieldsByPage.size
            ? prev
            : ((prev + 1) as PageNumbers[number]);
        setPagesVisited((prev) => new Set(prev).add(nextPage));
        return nextPage;
      });
    }
  };

  const isCurrentPageComplete = isPageComplete(page);

  const onClickBack = () => {
    setPage((page) => {
      if (page === 1) {
        return page;
      }
      return (page - 1) as typeof page;
    });
  };

  return (
    <>
      <EuiStepsHorizontal
        steps={Array.from(fieldsByPage.keys()).map((page) => ({
          key: `page-${page}`,
          title: fieldsByPage.get(page)?.title,
          onClick: () => handleSetPage(page),
          status: getTimelineStatus(page),
          disabled: (() => {
            if (page === 1) {
              return false;
            }
            return Array.from(fieldsByPage.keys())
              .filter((otherFormPages) => otherFormPages < page)
              .map((page) => isPageComplete(page) && pagesVisited.has(page))
              .some((isComplete) => !isComplete);
          })(),
        }))}
      />
      <EuiSpacer size="m" />
      <>
        <EuiForm
          css={css`
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            .euiFormRow {
              min-width: 400px;
            }
            .euiSelectable {
              .euiFormControlLayoutIcons {
                display: none;
              }
              .euiFieldSearch {
                padding-left: 12px;
              }
            }
          `}
        >
          <FormCreatePersonalSpaceFields
            page={page}
            fieldsByPage={fieldsByPage}
          />
          <EuiSpacer />
          <EuiFlexGroup>
            {page === fieldsByPage.size ? (
              <>
                <EuiButton isDisabled={page === 1} onClick={onClickBack}>
                  Back
                </EuiButton>
                <EuiButton
                  fill
                  isDisabled={!isValid}
                  onClick={() => {
                    handleSubmit();
                  }}
                  isLoading={isSubmitting}
                >
                  Create
                </EuiButton>
              </>
            ) : (
              <>
                <EuiButton isDisabled={page === 1} onClick={onClickBack}>
                  Back
                </EuiButton>
                <EuiButton
                  fill
                  isDisabled={!isCurrentPageComplete}
                  title={
                    !isCurrentPageComplete
                      ? "Complete all required fields before moving on"
                      : "Next Step"
                  }
                  onClick={() => handleSetPage()}
                >
                  Next
                </EuiButton>
              </>
            )}
          </EuiFlexGroup>
        </EuiForm>
      </>
    </>
  );
};
